import { createEffect, onCleanup, type Accessor } from "solid-js";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";

export const combineDestructors = combine;

/** Like `onCleanup`, but cleans up multiple functions with `combineDestructors` */
export function onCombinedCleanup(...fns: Parameters<typeof combineDestructors>): void {
  onCleanup(combineDestructors(...fns));
}

/** Helpful debug function to print to console when a reactive value changes */
export function createDebug(source: Accessor<unknown>, label = "Unlabeled value") {
  if (!import.meta.env.PROD) {
    const [labelColor, timestampColor, valueColor] = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches
      ? ["#EBEB1E", "#35F271", "#F235E8"]
      : ["#808012", "#1B7D3D", "#7A1A75"];
    const colors = [labelColor, "inherit", timestampColor, "inherit", valueColor].map(
      (value) => `color: ${value}`,
    );

    createEffect(() => {
      console.log(
        `%c${label} %cchanged at %c${new Date().toISOString()}%c: %c${source() as string}`,
        ...colors,
      );
    });
  }
}

/** Used to narrow a discriminated union to a specific value */
export function createMatcher<const D extends PropertyKey>(discriminant: D) {
  return function match<V extends Record<D, unknown>, const DV extends V[D]>(
    value: V | null | undefined,
    discriminantValue: DV,
  ): Extract<V, Record<D, DV>> | undefined {
    return value?.[discriminant] === discriminantValue
      ? (value as Extract<V, Record<D, DV>>)
      : undefined;
  };
}

export const isKind = createMatcher("kind");
export const isType = createMatcher("type");
export const isStatus = createMatcher("status");

/**
 * Narrow a discriminated union by prefix-matching the discriminant. Mirrors
 * `createMatcher` for template-literal types, e.g. Vercel AI SDK's
 * `tool-${name}` parts.
 */
function createPrefixMatcher<const D extends PropertyKey>(discriminant: D) {
  return function match<V extends Record<D, unknown>, const P extends string>(
    value: V | null | undefined,
    prefix: P,
  ): Extract<V, Record<D, `${P}${string}`>> | undefined {
    const v = value?.[discriminant];
    if (typeof v !== "string" || !v.startsWith(prefix)) return undefined;
    return value as Extract<V, Record<D, `${P}${string}`>>;
  };
}

export const isTypePrefix = createPrefixMatcher("type");

/** Used to narrow a discriminated union by excluding a specific value */
export function createNotMatcher<const D extends PropertyKey>(discriminant: D) {
  return function notMatch<Union extends Record<D, unknown>, const DV extends Union[D]>(
    value: Union,
    discriminantValue: DV,
  ) {
    return value[discriminant] !== discriminantValue
      ? (value as Exclude<Union, Record<D, DV>>)
      : false;
  };
}

export const isNotKind = createNotMatcher("kind");

/**
 * Combines an internal ref callback with a forwarded ref prop.
 *
 * @param setter - Callback to set your internal ref
 * @param forwardRef - The ref prop to forward (from props.ref)
 *
 * @example
 * let dialogRef: HTMLDialogElement | undefined;
 *
 * <dialog ref={assignRefs((el) => dialogRef = el, props.ref)} />
 */
export function assignRefs<T>(
  setter: (el: T) => void,
  forwardRef?: T | ((el: T) => void),
): (el: T) => void {
  return (el) => {
    setter(el);
    if (forwardRef) {
      // solid transforms the passed in ref prop so it is always the callback form
      (forwardRef as (el: T) => void)(el);
    }
  };
}
